import {useMemo} from 'react';

/**
 * Hook that manages product images for the carousel and thumbnails
 * @param {Object} options - Configuration options for the hook
 * @param {Function} options.buildColorImage - Function to generate color-specific image URLs
 * @param {Object} options.carouselProps - Properties containing main content and thumbnail images
 * @param {Object} options.currentColor - Currently selected color object
 * @param {boolean} options.skipColorImages - Flag to not add color-specific images
 * @param {string} options.styleName - Name of the product style
 */
export default function useProductImages({
  buildColorImage,
  carouselProps,
  currentColor,
  skipColorImages = false,
  styleName
}) {
  const buildColorImageSet = useMemo(() => {
    if (!currentColor || !buildColorImage) {
      return '';
    }

    return `${buildColorImage(currentColor, 722)} 1x, ${buildColorImage(currentColor, 900)} 2x`;
  }, [currentColor, buildColorImage]);

  const carouselImagesData = useMemo(() => {
    if (!styleName || !carouselProps?.mainContent) {
      return [];
    }

    const images = [];

    if (!skipColorImages && currentColor && buildColorImage) {
      images.push({
        alt: `${styleName}-default-preview`,
        src: buildColorImage(currentColor),
        srcSet: buildColorImageSet
      });
    }

    images.push(
      ...carouselProps.mainContent.map((media) => ({
        alt: styleName,
        src: media.src,
        srcSet: media.srcset
      }))
    );

    return images;
  }, [
    styleName,
    currentColor,
    buildColorImage,
    carouselProps?.mainContent,
    buildColorImageSet,
    skipColorImages
  ]);

  const thumbnailImagesData = useMemo(() => {
    if (!styleName || !carouselProps?.thumbnails) {
      return [];
    }

    const images = [];

    if (!skipColorImages && currentColor && buildColorImage) {
      images.push({
        alt: `${styleName}-default`,
        src: buildColorImage(currentColor),
        srcSet: buildColorImageSet
      });
    }

    images.push(
      ...carouselProps.thumbnails.map((media) => ({
        alt: styleName,
        src: media.src,
        srcSet: media.srcset
      }))
    );

    return images;
  }, [
    styleName,
    currentColor,
    buildColorImage,
    carouselProps?.thumbnails,
    buildColorImageSet,
    skipColorImages
  ]);

  return {
    carouselImagesData,
    thumbnailImagesData
  };
}
